/**
 * Beta languages and voice genders, where the portal chooses or lists a language or a voice (plan-026, owner
 * decisions 2026-10-21, migration 0176).
 *
 * WHICH LANGUAGES ARE BETA IS DATA. `language_pack.maturity` arrives on every `GET /v1/languages` row
 * (`LanguagePackView.maturity`); this file names no language. Ending a language's Beta is one migration that sets its
 * maturity to `general` — the badge and the note then disappear with no portal change.
 *
 *   · {@link LanguageBetaTag} — the «Beta» badge beside a beta language's name: the existing outline `Tag` (as the
 *     History row's "Test" and an order payment's test tag draw it), no new colour.
 *   · {@link languageBetaNoteOf} — the note where languages are chosen, built from the beta languages offered there:
 *     "Spanish and Portuguese are in beta: wording is still being reviewed." / «Τα ισπανικά και τα πορτογαλικά είναι
 *     σε δοκιμαστική έκδοση (beta): η διατύπωση ελέγχεται ακόμη.» — `null` when none is offered, so nothing is drawn.
 *   · {@link betaLanguageLabelOf} — the same mark in plain text, for an `<option>` or a joined line where a badge
 *     cannot sit: "Spanish (Beta)".
 *   · {@link voiceGenderTextOf} — "Female" / "Male" (Γυναικεία / Ανδρική) beside a voice's name; `null` for a voice
 *     whose gender was not recorded, so nothing is shown.
 */
import { Tag } from "@wetalk/ui";
import type { ReactNode } from "react";

import { languageNameOf } from "../api/conversation-view.js";
import type { LanguagePackView, VoiceGender } from "../api/gateway.js";
import { portalLocale, portalText, usePortalTranslation } from "../i18n/portal.js";

/** Whether `code` is a beta language of the catalogue. A language the catalogue does not hold is not marked. */
export function isBetaLanguage(pack: readonly LanguagePackView[] | undefined, code: string | null): boolean {
  if (code === null || pack === undefined) return false;
  return pack.some((one) => one.code === code && one.maturity === "beta");
}

/** The «Beta» badge. */
export function LanguageBetaTag(): ReactNode {
  usePortalTranslation("shell");
  return <Tag tone="outline">{portalText("shell", "language_beta.tag")}</Tag>;
}

/** The badge when `code` is a beta language, else nothing. */
export function LanguageBetaMark({ pack, code }: { readonly pack: readonly LanguagePackView[] | undefined; readonly code: string | null }): ReactNode {
  return isBetaLanguage(pack, code) ? <LanguageBetaTag /> : null;
}

/** "Spanish (Beta)" for a beta language, the name unchanged otherwise. */
export function betaLanguageLabelOf(pack: readonly LanguagePackView[] | undefined, code: string | null, name: string): string {
  return isBetaLanguage(pack, code) ? portalText("shell", "language_beta.labelled", { name }) : name;
}

/** A language's name in the portal's language, as a sentence names it (Greek writes it in lowercase: «τα ισπανικά»). */
function nameInSentenceOf(one: LanguagePackView): string {
  const shown = languageNameOf(one.code);
  const name = shown === one.code ? one.name : shown;
  return portalLocale() === "el" ? name.toLocaleLowerCase("el") : name;
}

/**
 * The note for the languages `offered` where a language is chosen: the beta ones, in the order offered, joined as the
 * portal's language joins a list. `null` when none of them is beta.
 */
export function languageBetaNoteOf(offered: readonly LanguagePackView[]): string | null {
  const beta = offered.filter((one) => one.maturity === "beta");
  if (beta.length === 0) return null;
  const locale = portalLocale();
  const names = beta.map((one) => portalText("shell", "language_beta.note_name", { name: nameInSentenceOf(one) }));
  const languages = new Intl.ListFormat(locale === "el" ? "el-GR" : "en-GB", { type: "conjunction" }).format(names);
  const note = portalText("shell", "language_beta.note", { count: beta.length, languages });
  return `${note.charAt(0).toLocaleUpperCase(locale)}${note.slice(1)}`;
}

/** "Female" / "Male" in the portal's language; `null` when the voice's gender was not recorded. */
export function voiceGenderTextOf(gender: VoiceGender | null): string | null {
  if (gender === null) return null;
  return gender === "female" ? portalText("shell", "voice_gender.female") : portalText("shell", "voice_gender.male");
}
